/* eslint-disable @typescript-eslint/no-explicit-any */
import './broker-tabs.scss';

import { Box, Container, Stack, Tab, Tabs } from '@mui/material';
import { CircularLoader, NewConvergeButton } from '../../../../presentation';
import {
  ContactType,
  LicensePaginationSort,
  LicenseSortKey,
  SortOrder,
  UserGroup,
  useCheckDuplicateEmail,
  useGetBroker,
  useUpsertBrokerageMutation,
} from '@converge/graphql';
import {
  FieldErrors,
  FormProvider,
  SubmitHandler,
  useForm,
} from 'react-hook-form';
import React, { useCallback, useEffect, useMemo, useState } from 'react';
import {
  useConvergeNavigation,
  useConvergeSnackBar,
} from '../../../../contexts';
import { useNavigate, useParams } from 'react-router-dom';

import { ApolloError } from '@apollo/client';
import { BrokerBrokerageHeader } from './broker-brokerage-header';
import BrokerFormAccordion from './accordion.broker-form-container';
import { BrokerNoteContainer } from './broker-note-container';
import { CreateBrokerField } from '@converge/data';
import { CreateBrokerFieldsSchema } from './broker-form.validation.schema';
import { DeleteModal } from '../../../brokerage/brokerage-tabs';
import { ReactiveBrokerBrokerage } from '../../../brokerage/reactive-broker-brokerage';
import debounce from 'lodash.debounce';
import { scrollToFirstError } from '@converge/utils';
import { yupResolver } from '@hookform/resolvers/yup';

const normalizePhoneToE164 = (phone: string): string => {
  if (!phone) return '';

  const cleaned = phone.replace(/[^\d+]/g, '');
  if (
    (cleaned.startsWith('+1') && cleaned.length === 12) ||
    cleaned.startsWith('+')
  ) {
    return cleaned;
  }
  if (cleaned.length === 10) {
    return `+1${cleaned}`;
  }
  if (cleaned.length === 11 && cleaned.startsWith('1')) {
    return `+${cleaned}`;
  }
  return phone;
};

interface TabPanelProps {
  children?: React.ReactNode;
  index: number;
  value: number;
}

export interface CreateBrokerFields {
  [CreateBrokerField.FirstName]: string;
  [CreateBrokerField.LastName]: string;
  [CreateBrokerField.Ssn]: string;
  [CreateBrokerField.Npn]: string;
  [CreateBrokerField.GlobalParent]: string;
  [CreateBrokerField.Office]: string;
  [CreateBrokerField.Email]: string;
  [CreateBrokerField.SecondaryEmail]: string | undefined | null;
  [CreateBrokerField.TertiaryEmail]: string | undefined | null;
  [CreateBrokerField.OfficePhoneNumber]: string | undefined | null;
  [CreateBrokerField.OfficePhoneNumberExtn]: string | undefined | null;
  [CreateBrokerField.MobilePhoneNumber]: string | undefined | null;
  [CreateBrokerField.MobilePhoneNumberExtn]: string | undefined | null;
  [CreateBrokerField.Address1]: string | undefined | null;
  [CreateBrokerField.Address2]: string | undefined | null;
  [CreateBrokerField.City]: string | undefined | null;
  [CreateBrokerField.Underwriter]: string | undefined | null;
  [CreateBrokerField.BusinessDevelopmentManager]: string | undefined | null;
  [CreateBrokerField.UwRegion]: string | undefined | null;
  [CreateBrokerField.RegionUuid]: string | undefined | null;
  [CreateBrokerField.PostalCode]: string | undefined | null;
  [CreateBrokerField.CountryUuid]: string | undefined | null;
  [CreateBrokerField.BdmRegion]: string | undefined | null;
  [CreateBrokerField.BrokerTitle]: string | undefined | null;
  [CreateBrokerField.BrokerTeam]: string | undefined | null;
}

function TabPanel(props: TabPanelProps) {
  const { children, value, index, ...other } = props;
  return (
    <div
      role="tabpanel"
      hidden={value !== index}
      id={`simple-tabpanel-${index}`}
      aria-labelledby={`simple-tab-${index}`}
      {...other}
      style={{ overflow: `${value === 1 ? 'auto' : 'hidden'}` }}
    >
      {value === index && (
        <Box
          className={`${
            value === 1 ? 'broker-tabs-wrapper' : 'broker-tabs-wrapper2'
          }`}
        >
          {children}
        </Box>
      )}
    </div>
  );
}

function a11yProps(index: number) {
  return {
    id: `simple-tab-${index}`,
    'aria-controls': `simple-tabpanel-${index}`,
  };
}

type ClaimEntityDetailParams = {
  brokerUuid: string;
};
const BrokerTabs = () => {
  const [value, setValue] = useState(0);
  const [open, setOpen] = useState(false);
  const [reactiveOpen, setReactiveOpen] = useState(false);
  const [firstRender, setFirstRender] = useState(true);
  const [hasInitializedForm, setHasInitializedForm] = useState(false);

  const navigate = useConvergeNavigation();
  const { notify } = useConvergeSnackBar();
  const handleChange = (event: React.SyntheticEvent, newValue: number) => {
    setValue(newValue);
  };
  const [licenseSort, setLicenseSort] = useState<LicensePaginationSort>({
    key: LicenseSortKey.Type,
    order: SortOrder.Asc,
  });
  const { brokerUuid } = useParams<keyof ClaimEntityDetailParams>();

  const { loading: brokerLoading, broker } = useGetBroker({
    uuid: brokerUuid,
    enabled: Boolean(brokerUuid),
    licenseSort: licenseSort,
  });
  const [primary, setPrimary] = useState<boolean | undefined>();
  const [isUserBdm, setIsUserBdm] = useState(false);
  const [isUserUnderwriter, setIsUserUnderwriter] = useState(false);

  const formMethods = useForm<CreateBrokerFields>({
    defaultValues: {},
    mode: 'all',
    reValidateMode: 'onChange',
    resolver: yupResolver(CreateBrokerFieldsSchema),
    // shouldUseNativeValidation: false,
  });

  useEffect(() => {
    if (broker[0]) {
      setFirstRender(false);
    }
  }, [broker]);
  const { watch, clearErrors, setError } = formMethods;
  const { isDirty, dirtyFields, errors: formErrors } = formMethods.formState;
  const email = watch(CreateBrokerField.Email);
  const currentEmail = broker[0]?.email;
  const emailUuid = broker[0]?.emailUuid;

  const setOrClearEmailDuplicateError = useCallback(
    (checkDuplicateEmail: boolean) => {
      if (checkDuplicateEmail) {
        setError('email', {
          type: 'duplicate',
          message: 'This email is already registered',
        });
      } else {
        clearErrors('email');
      }
    },
    [setError, clearErrors]
  );

  const { refetch } = useCheckDuplicateEmail({ enabled: false });
  const debouncedCheck = useMemo(
    () =>
      debounce(
        async (val: string, formErrors: FieldErrors<CreateBrokerFields>) => {
          const isCurrentEmail = currentEmail === val;
          if (!formErrors.email && !isCurrentEmail) {
            const { data } = await refetch({
              filters: { value: val, uuid: emailUuid },
            });

            setOrClearEmailDuplicateError(data.checkDuplicateEmail);
          }
        },
        1000
      ),
    [refetch, emailUuid, currentEmail, setOrClearEmailDuplicateError]
  );

  useEffect(() => {
    if (email && email !== currentEmail && dirtyFields.email) {
      debouncedCheck(email, formErrors);
    }
  }, [email, debouncedCheck, formErrors, currentEmail, dirtyFields.email]);

  useEffect(() => {
    return () => {
      debouncedCheck.cancel();
    };
  }, [debouncedCheck]);

  const navigates = useNavigate();
  useEffect(() => {
    // Only initialize form once when broker data is first loaded
    // This prevents form reset during refetch operations (like license sorting)

    if (broker[0] && !brokerLoading && !hasInitializedForm) {
      if (broker[0]?.primaryContact) {
        setPrimary(broker[0]?.primaryContact);
      }

      formMethods.setValue(CreateBrokerField.FirstName, broker[0]?.firstName);
      formMethods.setValue(CreateBrokerField.LastName, broker[0]?.lastName);
      formMethods.setValue(CreateBrokerField.BrokerTitle, broker[0]?.title);
      formMethods.setValue(
        CreateBrokerField.GlobalParent,
        broker[0]?.brokerage
      );
      formMethods.setValue(CreateBrokerField.Npn, broker[0]?.npn ?? '');

      const BusinessDevelopmentManagerUuid = broker[0]?.user?.edges?.filter(
        (bDMuser) => bDMuser?.node?.type === UserGroup.Bdm
      )[0]?.node?.uuid;

      const underwriterUuid = broker[0]?.user?.edges?.filter(
        (bDMuser) => bDMuser?.node?.type === UserGroup.Underwriter
      )[0]?.node?.uuid;

      if (underwriterUuid) {
        // Broker has a direct underwriter assignment
        setIsUserUnderwriter(true);
        formMethods.setValue(CreateBrokerField.Underwriter, underwriterUuid);
      } else {
        // Cascade UW: Team > Office > Brokerage
        formMethods.setValue(
          CreateBrokerField.Underwriter,
          broker[0]?.teamUW ||
            broker[0]?.officeUW ||
            broker[0]?.brokerageUW ||
            ''
        );
      }

      if (BusinessDevelopmentManagerUuid) {
        // Broker has a direct BDM assignment
        setIsUserBdm(true);
        formMethods.setValue(
          CreateBrokerField.BusinessDevelopmentManager,
          BusinessDevelopmentManagerUuid
        );
      } else {
        // Cascade BDM: Team > Office > Brokerage
        formMethods.setValue(
          CreateBrokerField.BusinessDevelopmentManager,
          broker[0]?.teamDBM ||
            broker[0]?.officeDBM ||
            broker[0]?.brokerageDBM ||
            ''
        );
      }
      formMethods.setValue(
        CreateBrokerField.UwRegion,
        broker[0]?.uWRegion || ''
      );
      formMethods.setValue(
        CreateBrokerField.BdmRegion,
        broker[0]?.bdmRegion || ''
      );

      // Set BrokerTeam and Office based on GlobalParent condition
      if (broker[0]?.brokerage) {
        formMethods.setValue(
          CreateBrokerField.BrokerTeam,
          broker[0]?.teamUuid ?? ''
        );
        formMethods.setValue(
          CreateBrokerField.Office,
          broker[0]?.officeUuid ?? ''
        );
      } else {
        formMethods.setValue(CreateBrokerField.BrokerTeam, '');
        formMethods.setValue(CreateBrokerField.Office, '');
      }

      setHasInitializedForm(true);
    } else if (!hasInitializedForm) {
      formMethods.setValue(CreateBrokerField.BrokerTeam, '');
      formMethods.setValue(CreateBrokerField.Office, '');
    }
    if (broker[0]?.email) {
      formMethods.setValue(CreateBrokerField.Email, broker[0]?.email ?? '');
    }
    if (broker[0]?.secondaryEmail) {
      formMethods.setValue(
        CreateBrokerField.SecondaryEmail,
        broker[0]?.secondaryEmail ?? ''
      );
    }
    if (broker[0]?.tertiaryEmail) {
      formMethods.setValue(
        CreateBrokerField.TertiaryEmail,
        broker[0]?.tertiaryEmail ?? ''
      );
    }
    if (broker[0]?.phone) {
      const safeNormalizePhone = (val?: string) =>
        normalizePhoneToE164(val?.split('x')[0] ?? '');
      formMethods.setValue(
        CreateBrokerField.MobilePhoneNumber,
        safeNormalizePhone(broker[0]?.phone)
      );

      if (broker[0]?.phone?.includes('x')) {
        formMethods.setValue(
          CreateBrokerField.MobilePhoneNumberExtn,
          broker[0]?.phone.split('x')[1] ?? ''
        );
      }
      if (broker[0]?.officePhone) {
        formMethods.setValue(
          CreateBrokerField.OfficePhoneNumber,
          safeNormalizePhone(broker[0]?.officePhone)
        );
      }

      if (broker[0]?.officePhone?.includes('x')) {
        formMethods.setValue(
          CreateBrokerField.OfficePhoneNumberExtn,
          broker[0]?.officePhone.split('x')[1] ?? ''
        );
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [formMethods, broker, brokerUuid, brokerLoading]);

  const [isDisabledPrimary, setIsDisabledPrimary] = useState(
    brokerUuid && broker[0]?.teamUuid ? false : true
  );

  const brokerName = `${formMethods?.getValues(
    CreateBrokerField.FirstName
  )} ${formMethods?.getValues(CreateBrokerField.LastName)}`;

  const handleLicenseSortChange = (sortModel: LicensePaginationSort) => {
    setLicenseSort(sortModel);
  };
  const isReadOnly = broker[0]?.status === 'INACTIVE';
  const { upsertEntity, loading } = useUpsertBrokerageMutation();
  const onSubmitHandler = useCallback(
    (): SubmitHandler<CreateBrokerFields> => async (fields) => {
      let brokerResponse, brokerResponseErrors;

      const { officeNumber, officeNumberExtn, mobileNumber, mobileNumberExtn } =
        fields;
      const brokerData = {
        entityUuid: fields?.globalParent,
        persons: [
          {
            primaryContact: primary ?? false,
            contacts: [],
          },
        ],
      } as any;

      const updateBrokerData = (
        fieldName: string,
        formFieldName: CreateBrokerField,
        fieldValue: any
      ) => {
        if (dirtyFields[formFieldName]) {
          brokerData.persons[0][fieldName] = fieldValue;
        }
      };

      if (brokerUuid) {
        brokerData.persons[0].personUuid = brokerUuid;
      } else {
        brokerData.persons[0].status = 'ACTIVE';
        brokerData.persons[0].agent = true;
      }

      updateBrokerData(
        'firstName',
        CreateBrokerField.FirstName,
        fields?.firstName
      );

      updateBrokerData(
        'lastName',
        CreateBrokerField.LastName,
        fields?.lastName
      );

      updateBrokerData('npn', CreateBrokerField.Npn, fields?.entityNpn);

      updateBrokerData(
        'officeUuid',
        CreateBrokerField.Office,
        fields?.office && fields?.office.length ? fields?.office : 'null'
      );

      // Only save BDM if user manually selected it (isUserBdm is true)
      // If BDM is inherited from team/office/brokerage, don't save it
      if (isUserBdm) {
        updateBrokerData(
          'bdmUserUuid',
          CreateBrokerField.BusinessDevelopmentManager,
          fields?.bdmUserUuid && fields?.bdmUserUuid.length
            ? fields?.bdmUserUuid
            : 'null'
        );
      } else {
        // Explicitly set to null if there's no user-selected BDM
        // This ensures we don't save inherited BDM values
        updateBrokerData(
          'bdmUserUuid',
          CreateBrokerField.BusinessDevelopmentManager,
          'null'
        );
      }
      updateBrokerData(
        'uwTerritoryUuids',
        CreateBrokerField.UwRegion,
        fields?.uwTerritoryUuids && fields?.uwTerritoryUuids.length
          ? [fields?.uwTerritoryUuids]
          : []
      );
      updateBrokerData(
        'bdmTerritoryUuids',
        CreateBrokerField.BdmRegion,
        fields?.bdmTerritoryUuids && fields?.bdmTerritoryUuids.length
          ? [fields?.bdmTerritoryUuids]
          : []
      );
      // Only save underwriter if user manually selected it (isUserUnderwriter is true)
      // If underwriter is inherited from team/office/brokerage, don't save it
      if (isUserUnderwriter) {
        updateBrokerData(
          'underwriterUserUuid',
          CreateBrokerField.Underwriter,
          fields?.underwriterUserUuid && fields?.underwriterUserUuid.length
            ? fields?.underwriterUserUuid
            : 'null'
        );
      } else {
        // Explicitly set to null if there's no user-selected underwriter
        // This ensures we don't save inherited underwriter values
        updateBrokerData(
          'underwriterUserUuid',
          CreateBrokerField.Underwriter,
          'null'
        );
      }
      updateBrokerData(
        'teamUuids',
        CreateBrokerField.BrokerTeam,
        fields?.brokerTeam && fields?.brokerTeam.length
          ? fields?.brokerTeam
          : ''
      );
      updateBrokerData(
        'title',
        CreateBrokerField.BrokerTitle,
        fields?.title && fields?.title.length ? fields?.title : 'null'
      );

      if (dirtyFields.email) {
        brokerData.persons[0].contacts.push({
          type: ContactType.Email,
          value: fields?.email || '',
          source: 'PRIMARY',
          contactUuid: brokerUuid ? broker[0].emailUuid : null,
        });
      }

      if (dirtyFields.secondaryEmail) {
        brokerData.persons[0].contacts.push({
          type: ContactType.Email,
          value: fields?.secondaryEmail || '',
          source: 'CONVERGE',
          contactUuid: brokerUuid ? broker[0].secondaryEmailUuid : null,
        });
      }

      if (dirtyFields.tertiaryEmail) {
        brokerData.persons[0].contacts.push({
          type: ContactType.Email,
          value: fields?.tertiaryEmail || '',
          source: 'CONVERGE',
          contactUuid: brokerUuid ? broker[0].tertiaryEmailUuid : null,
        });
      }

      if (officeNumber || officeNumber === '') {
        brokerData.persons[0].contacts.push({
          type: ContactType.Office,
          value:
            officeNumber && officeNumberExtn
              ? `${officeNumber}x${officeNumberExtn}`
              : officeNumber || '',
          contactUuid: brokerUuid ? broker[0]?.officePhoneUuid : null,
        });
      }
      if (mobileNumber || mobileNumber === '') {
        brokerData.persons[0].contacts.push({
          type: ContactType.Phone,
          value:
            mobileNumber && mobileNumberExtn
              ? `${mobileNumber}x${mobileNumberExtn}`
              : mobileNumber || '',
          contactUuid: brokerUuid ? broker[0]?.phoneUuid : null,
        });
      }

      try {
        // Only check for duplicate email if email field was actually changed
        if (dirtyFields.email) {
          const { data: dataCheckEmail } = await refetch({
            filters: { value: fields?.email, uuid: emailUuid },
          });
          if (dataCheckEmail.checkDuplicateEmail) {
            return setOrClearEmailDuplicateError(
              dataCheckEmail?.checkDuplicateEmail
            );
          }
        }
        const { data, errors } = await upsertEntity({
          variables: { entityInput: brokerData },
        });

        brokerResponse = data;
        brokerResponseErrors = errors;

        notify(`Broker has been ${brokerUuid ? 'updated' : 'added'}`, {
          variant: 'success',
        });

        if (brokerUuid) {
          navigates(`/producers/broker/${brokerUuid}`);
        } else {
          navigate.producerBrokers();
        }
      } catch (cause) {
        console.error(
          `Failed to ${
            brokerUuid ? 'update' : 'save'
          } broker. Please try again. ${cause}`
        );
        if (cause instanceof ApolloError) {
          brokerResponseErrors = cause.graphQLErrors;
        }
      }
      const errorMessage = brokerResponseErrors?.[0]?.message;
      if (errorMessage || !brokerResponse) {
        notify(
          `Failed to ${
            brokerUuid ? 'update' : 'save'
          } broker. Please try again. (${
            errorMessage || 'No data returned by the API'
          })`,
          {
            variant: 'error',
          }
        );
        return;
      }
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [
      upsertEntity,
      notify,
      navigate,
      navigates,
      brokerUuid,
      broker,
      primary,
      dirtyFields,
      isUserBdm,
      isUserUnderwriter,
    ]
  );

  if (brokerLoading && firstRender) {
    return <CircularLoader />;
  }

  if (brokerUuid && !broker[0] && !brokerLoading) {
    return <p className="broker-message">Broker not found</p>;
  }
  return (
    <Container
      maxWidth={false}
      sx={{
        background: 'white',
        overflow: value === 0 ? 'auto' : 'hidden',
      }}
      disableGutters
    >
      <Box
        className="form-container"
        sx={{
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
        }}
      >
        <BrokerBrokerageHeader
          active={!isReadOnly}
          showStatus={!!brokerUuid}
          isBroker={true}
          onArrowClick={() => navigate.producerBrokers()}
          value={value}
          uuid={brokerUuid}
          name={brokerName}
          onClickDeactivate={() => setOpen(true)}
          onClickReactivate={() => setReactiveOpen(true)}
        />
      </Box>

      <Box>
        <Tabs
          value={value}
          onChange={handleChange}
          aria-label="simple tabs example"
          sx={{
            fontWeight: 'bold',
            color: '#000000 !important',
            borderBottom: '1px solid #000000',
            margin: '0 52px',
            '& .Mui-selected': {
              fontWeight: 'bold',
              color: '#000000 !important',
            },
          }}
          variant="fullWidth"
        >
          <Tab
            className="broker-tab"
            label="Broker Information"
            data-testid="broker-information-tab"
            {...a11yProps(0)}
          />
          <Tab
            className="broker-tab"
            style={{ overflow: 'hidden' }}
            label="Broker Notes"
            data-testid="broker-notes-tab"
            {...a11yProps(1)}
          />
        </Tabs>
        <TabPanel value={value} index={0}>
          <Stack className="form-tab">
            <FormProvider {...formMethods}>
              <form className="form-wrapper-style">
                <Box className="form-container">
                  <BrokerFormAccordion
                    formMethods={formMethods}
                    setPrimary={setPrimary}
                    brokerUuid={brokerUuid}
                    isReadOnly={isReadOnly}
                    onSubmitHandler={onSubmitHandler}
                    brokerLicense={broker[0]?.licenses || []}
                    associateBrokerageUuid={broker[0]?.brokerage}
                    primary={primary}
                    setIsDisabledPrimary={setIsDisabledPrimary}
                    isDisabledPrimary={isDisabledPrimary}
                    brokerName={brokerName}
                    isUserSetBdm={isUserBdm}
                    setIsUserBdm={setIsUserBdm}
                    setIsUserUnderwriter={setIsUserUnderwriter}
                    isUserUnderwriter={isUserUnderwriter}
                    isLoading={brokerLoading}
                    onLicenseSortChange={handleLicenseSortChange}
                  />
                </Box>
              </form>
            </FormProvider>
          </Stack>
        </TabPanel>
        <Stack className="btn-container">
          <Box className="cancel-btn">
            <NewConvergeButton
              dataTestId="cancel-button"
              type="reset"
              label={'Cancel'}
              variant={'outlined'}
              onClick={() => navigate.producerBrokers()}
            />
          </Box>
          <Box className="add-btn">
            <NewConvergeButton
              dataTestId={
                brokerUuid ? 'update-broker-button' : 'save-broker-button'
              }
              type="submit"
              label={brokerUuid ? 'Update Broker' : 'Save Broker'}
              loading={loading}
              disabled={isReadOnly || !isDirty}
              onClick={() => {
                formMethods.handleSubmit(onSubmitHandler())();
                scrollToFirstError(formMethods);
              }}
            />
          </Box>
        </Stack>
        <TabPanel value={value} index={1}>
          {brokerUuid ? (
            <BrokerNoteContainer brokerUuid={brokerUuid} />
          ) : (
            <Box
              className="broker-message-container"
              data-testid="save-broker-info-message"
            >
              Please save the broker information <br /> before including any
              notes.
            </Box>
          )}
        </TabPanel>
      </Box>

      <DeleteModal
        open={open}
        onClose={() => {
          setOpen(false);
        }}
        entityUuid={broker[0]?.brokerage}
        personUuid={brokerUuid}
      />
      <ReactiveBrokerBrokerage
        open={reactiveOpen}
        onClose={() => {
          setReactiveOpen(false);
        }}
        entityUuid={broker[0]?.brokerage}
        personUuid={brokerUuid}
      />
    </Container>
  );
};

export default BrokerTabs;
